import type { LocalHashtag, Resolvers } from '../../../generated';
import {
  favoritedAssets,
  favoritedProjects,
  hashtags,
  openedProjectFolders,
} from './state';

export const resolvers: Resolvers = {
  /** here we specify resolvers for our local mutations */
  Mutation: {
    /** our local mutation. the second argument here contains the variables passed in */
    setProjectFavorite: (_root, { userId, projectId, favorited }) => {
      /** get the array of favorited projects for userId from our map.
       * initialize them to [] if they don't exist */
      const projects = favoritedProjects.get(userId, []);

      /** if we are favoriting a project, and it's not already in the array, add it to the favorited projects */
      if (favorited && !projects.includes(projectId)) {
        favoritedProjects.set(userId, (draft) => {
          /**
           * draft here contains the current array of favorited projects at `userId`
           *
           * (draft is a convenience proxy object from `immer`. it lets us
           * perform mutations like `push` while still maintaining immutability.
           * @see https://immerjs.github.io/immer/docs/introduction
           *
           * note that we must name this argument `draft` so ESLint knows it's okay to mutate
           * */
          draft.push(projectId);
        });
        return true; // the return type of the mutation is a boolean. true here because favorited is now true
      }

      /** if we are unfavoriting a project, we simply filter it out of the array */
      favoritedProjects.set(userId, (draft) =>
        /**
         * immer allows us to either mutate (as above) or return a new draft (as here)
         * @important you can't both mutate AND return a new object or immer will error
         */
        draft.filter((project) => project !== projectId)
      );
      return false; // we return false here because favorited is now false
    },

    setAssetFavorite: (
      _root,
      { userId, projectId, assetId, name, type, favorited, emoji }
    ) => {
      const userEntities = favoritedAssets.get(userId, []);

      const includesAsset = (asset) => {
        return asset.projectId === projectId && asset.id === assetId;
      };

      if (favorited && !userEntities.some(includesAsset)) {
        favoritedAssets.set(userId, (draft) => {
          draft.push({
            id: assetId,
            projectId,
            name,
            type,
            favorited,
            emoji: emoji || `⭐️`,
          });
        });

        return true; // favorited is now true
      }

      /** update the store when unfavoriting an asset. Filter it out of the array */
      favoritedAssets.set(userId, (draft) =>
        draft.filter(
          (asset) => asset.projectId === projectId && asset.id !== assetId
        )
      );
      return false;
    },

    setProjectFolderOpen: (_root, { userId, projectId, folderId }) => {
      const userFolders = openedProjectFolders.get(userId, []);

      const includesFolder = (folder) => {
        return folder.projectId === projectId && folder.id === folderId;
      };

      if (!userFolders.some(includesFolder)) {
        openedProjectFolders.set(userId, (draft) => {
          draft.push({
            id: folderId,
            projectId,
            isOpened: true,
          });
        });
        return true; // isOpen is now true
      }

      /** update the store when unfavoriting an asset. Filter it out of the array */
      openedProjectFolders.set(userId, (draft) =>
        draft.filter(
          (folder) => folder.projectId === projectId && folder.id !== folderId
        )
      );
      return false;
    },

    setHashtag: (_root: any, { projectId, hashtag }: any) => {
      /**
       * We assume new hashtags are set without an id to start. When they
       * are saved on the BE they'll have an associated id generated.
       */
      const newHashtag: LocalHashtag = {
        id: crypto.randomUUID(),
        name: hashtag,
      };

      const projectHashtags = hashtags.get(projectId, []);
      const hashtagIndex = projectHashtags.findIndex(
        ({ name }) => name === hashtag
      );
      const currentTag = projectHashtags[hashtagIndex];
      if (hashtagIndex > -1) {
        /**
         * If the hashtag parameter matches a hashtag that already exists,
         * move it to the top of the suggestion list
         */
        hashtags.set(projectId, (draft) => {
          // Saving currentTag to preserve the original hashtag.id
          draft.splice(hashtagIndex, 1);
          draft.unshift(currentTag);
        });
        return currentTag;
      }
      hashtags.set(projectId, (draft) => {
        draft.unshift(newHashtag);
        if (draft.length > 25) draft.pop();
      });
      return newHashtag;
    },
  },
};
